import {createSelector, type Selector} from "@reduxjs/toolkit";

import {PersonalizationSelectors} from "@atg-tillsammans-shared/personalization-data-access";

import {MemberTeamMapper} from "../utils/teamMember.mapper";

import {teamMemberSlice} from "./teamMember.slice";

import type {GlobalTeamMemberState} from "./teamMember.types";
import type {MemberTeam, TeamID} from "@atg-tillsammans/types";

export const selectTeamsState = (state: GlobalTeamMemberState) =>
    state[teamMemberSlice.name].teams;

export const selectTeams: Selector<GlobalTeamMemberState, MemberTeam[]> = createSelector(
    PersonalizationSelectors.getMemberId,
    selectTeamsState,
    (memberId, {data}) =>
        data?.myTeams?.map(MemberTeamMapper.toMemberTeamWithMemberId(memberId)) ?? [],
);

export const findTeam: (
    teamID: TeamID,
) => Selector<GlobalTeamMemberState, MemberTeam | null> = (teamId) =>
    createSelector(
        selectTeams,
        (teams: MemberTeam[]) => teams.find((team) => team.id === teamId) ?? null,
    );

export const hasUnreadMessages: (
    teamID: TeamID,
) => Selector<GlobalTeamMemberState, boolean> = (teamId) =>
    createSelector(findTeam(teamId), (team: MemberTeam | null) =>
        team && team.unreadComments ? team.unreadComments > 0 : false,
    );

export const isTeamMember: (
    teamID: TeamID,
) => Selector<GlobalTeamMemberState, boolean> = (teamId) =>
    createSelector(selectTeams, (teams: MemberTeam[]) =>
        teams.some((team) => team.id === teamId),
    );

export const selectTeamsCachedAt: Selector<GlobalTeamMemberState, number | null> =
    createSelector(selectTeamsState, ({cachedAt}) => cachedAt);
